import type { AnalyticsOverviewResponse } from '../types/analytics';
import type {
  EditorialZoneResponse,
  EditorPickQueueItem,
  EditorPickQueueResponse,
  EditorPickSuggestionsResponse,
} from '../types/editorial';
import type { ScorecardStatsResponse } from '../types/scorecardStats';
import type { FeedbackSubmission, FeedbackResponse } from '../types/feedback';
import type {
  GoalResponse,
  PaymentIntentResponse,
  SubscriptionResponse,
  CustomerPortalResponse,
  DonationHistoryItem,
  SubscriptionItem,
  CreatePaymentIntentRequest,
  CreateSubscriptionRequest,
} from '../types/donations';
import { getCurrentMlbSeason } from '../utils/constants';

// Re-export for convenience - components can import from api.ts
export { getCurrentMlbSeason as getCurrentSeasonYear };

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000') + '/api/v2';

// Default timeout for API requests (10 seconds)
const DEFAULT_TIMEOUT = 10000;

// Slow request threshold (ms) - requests slower than this are logged
const SLOW_REQUEST_THRESHOLD = 1000;

// Generate a unique request ID for correlation with backend logs
const generateRequestId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`;

// Helper to fetch with timeout and performance logging
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const requestId = generateRequestId();
  const startTime = performance.now();

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'X-Request-ID': requestId,
      },
      signal: controller.signal,
    });

    const duration = performance.now() - startTime;

    // Log slow requests in dev mode
    if (import.meta.env.DEV && duration > SLOW_REQUEST_THRESHOLD) {
      console.warn(
        `[PERF] Slow API call: ${url} took ${Math.round(duration)}ms (request_id: ${requestId})`
      );
    }

    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const VALID_TEMPLATE_KEYS = ['simple-portrait', 'detailed-landscape', 'reisner'] as const;
export type TemplateKey = (typeof VALID_TEMPLATE_KEYS)[number];

// Forward type declaration so this helper can be defined before its consumers.
interface ScorecardProfileFiltersForBuilder {
  season?: string | null;
  competition?: string | null;
  team?: string | null;
  game_type?: string | null;
  player?: string | null;
  sort?: string | null;
  page?: number;
  per_page?: number;
}

function buildProfileFilterQuery(filters: ScorecardProfileFiltersForBuilder): string {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.per_page) params.set('per_page', String(filters.per_page));
  if (filters.season) params.set('season', filters.season);
  if (filters.competition) params.set('competition', filters.competition);
  if (filters.team) params.set('team', filters.team);
  if (filters.game_type) params.set('game_type', filters.game_type);
  if (filters.player) params.set('player', filters.player);
  if (filters.sort) params.set('sort', filters.sort);
  return params.toString();
}

export const mlbApi = {
  // ==========================================================================
  // Gameday endpoints
  // ==========================================================================

  // Get calendar metadata (min/max dates and game counts)
  getCalendarMetadata: async (options?: { completedOnly?: boolean; level?: string }) => {
    const params = new URLSearchParams();
    if (options?.completedOnly) params.set('completed_only', 'true');
    if (options?.level) params.set('level', options.level);
    const qs = params.toString();
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/gameday/calendar-metadata${qs ? `?${qs}` : ''}`
    );
    if (!response.ok) throw new Error('Failed to fetch calendar metadata');
    return response.json();
  },

  // Get daily schedule
  getSchedule: async (date?: string, level?: string) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (level) params.set('level', level);
    const qs = params.toString();
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/gameday/schedule${qs ? `?${qs}` : ''}`
    );
    if (!response.ok) throw new Error('Failed to fetch schedule');
    return response.json();
  },

  // Get WBC schedule (by date or full tournament)
  getWBCSchedule: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await fetchWithTimeout(`${API_BASE_URL}/wbc/schedule${params}`);
    if (!response.ok) throw new Error('Failed to fetch WBC schedule');
    return response.json();
  },

  // Get game preview (scorecard prep) - reads from cache only
  getGamePreview: async (gamePk: number) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/gameday/preview/${gamePk}`);
    if (!response.ok) throw new Error('Failed to fetch game preview');
    return response.json();
  },

  // Refresh game preview cache from MLB API
  refreshGamePreview: async (gamePk: number) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/gameday/preview/${gamePk}/refresh`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to refresh game preview');
    return response.json();
  },

  // Check for lineup updates
  checkLineups: async (gamePk: number) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/gameday/preview/${gamePk}/check-lineups`,
      {
        method: 'POST',
      }
    );
    if (!response.ok) throw new Error('Failed to check lineups');
    return response.json();
  },

  // ==========================================================================
  // Box Score endpoints
  // ==========================================================================

  // Get box score for a game
  getBoxScore: async (gamePk: number) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/games/${gamePk}/boxscore`);
    if (!response.ok) throw new Error('Failed to fetch box score');
    return response.json();
  },

  // Get available box score columns (no auth required)
  getAvailableBoxScoreColumns: async () => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/box-score/available-columns`);
    if (!response.ok) throw new Error('Failed to fetch available columns');
    return response.json();
  },

  // Get players who appeared in a game (for MVP selection dropdown)
  getGamePlayers: async (gamePk: number) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/games/${gamePk}/players`);
    if (!response.ok) throw new Error('Failed to fetch game players');
    return response.json();
  },

  // Get MVP voting results for a game
  getGameMvpVotes: async (gameId: number) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/game/${gameId}/mvp-votes`);
    if (!response.ok) throw new Error('Failed to fetch MVP votes');
    return response.json();
  },

  // ==========================================================================
  // Scorecard Endpoints
  // ==========================================================================

  /**
   * Upload a new scorecard image.
   * Requires multipart/form-data.
   */
  uploadScorecard: async (formData: FormData, token?: string): Promise<ScorecardUploadResponse> => {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/upload`,
      {
        method: 'POST',
        headers,
        body: formData,
      },
      60000 // 60 second timeout for uploads
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Check if an image has already been uploaded for a game.
   */
  checkDuplicateScorecard: async (formData: FormData): Promise<DuplicateCheckResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/check-duplicate`,
      { method: 'POST', body: formData },
      30000
    );
    if (!response.ok) throw new Error('Failed to check duplicate');
    return response.json();
  },

  /**
   * Check if user already has a scorecard for a game.
   */
  checkExistingScorecard: async (
    gameId: number,
    token: string
  ): Promise<ExistingScorecardCheckResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/check-existing/${gameId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to check existing scorecard');
    return response.json();
  },

  /**
   * Get scorecards for a specific game.
   */
  getGameScorecards: async (
    gameId: number,
    params: { limit?: number; offset?: number } = {},
    token?: string
  ): Promise<ScorecardListResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/game/${gameId}${queryString}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch game scorecards');
    return response.json();
  },

  /**
   * Get scorecards for a user.
   */
  getUserScorecards: async (
    userId: number,
    params: { limit?: number; offset?: number } = {},
    token?: string
  ): Promise<ScorecardListResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/user/${userId}${queryString}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch user scorecards');
    return response.json();
  },

  /**
   * Get current user's scorecards.
   */
  getMyScorecards: async (
    params: { limit?: number; offset?: number } = {},
    token: string
  ): Promise<ScorecardListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/mine${queryString}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch my scorecards');
    return response.json();
  },

  /**
   * Get featured scorecards for homepage.
   */
  getFeaturedScorecards: async (limit: number = 20): Promise<FeaturedListResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/featured?limit=${limit}`);
    if (!response.ok) throw new Error('Failed to fetch featured scorecards');
    return response.json();
  },

  /**
   * Get single scorecard details.
   */
  getScorecardDetail: async (
    scorecardId: number,
    token?: string
  ): Promise<ScorecardDetailResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}`, {
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch scorecard');
    return response.json();
  },

  /**
   * Update scorecard metadata.
   */
  updateScorecard: async (
    scorecardId: number,
    data: {
      is_public?: boolean;
      attended_in_person?: boolean;
      caption?: string;
      viewing_method?: string | null;
      scoring_tool?: string | null;
      scorebook_type?: string | null;
      game_outcome?: string | null;
      mvp_player_mlb_id?: number | null;
      mvp_player_name?: string | null;
    },
    token: string
  ): Promise<{ status: string; scorecard_id: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update scorecard');
    return response.json();
  },

  /**
   * Delete a scorecard.
   */
  deleteScorecard: async (
    scorecardId: number,
    token: string
  ): Promise<{ status: string; scorecard_id: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to delete scorecard');
    return response.json();
  },

  /**
   * Toggle like on a scorecard.
   */
  toggleScorecardLike: async (
    scorecardId: number,
    token: string
  ): Promise<{ action: string; like_count: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}/like`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to toggle like');
    return response.json();
  },

  /**
   * Track a share action for analytics.
   */
  trackScorecardShare: async (
    scorecardId: number,
    platform: 'x' | 'facebook' | 'instagram' | 'tiktok' | 'link',
    token: string
  ): Promise<{ status: string; platform: string; new_count: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/${scorecardId}/share?platform=${platform}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to track share');
    return response.json();
  },

  /**
   * Get comments for a scorecard.
   */
  getScorecardComments: async (
    scorecardId: number,
    params: { limit?: number; offset?: number } = {}
  ): Promise<CommentListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/${scorecardId}/comments${queryString}`
    );
    if (!response.ok) throw new Error('Failed to fetch comments');
    return response.json();
  },

  /**
   * Add a comment to a scorecard.
   */
  addScorecardComment: async (
    scorecardId: number,
    body: string,
    token: string,
    parentId?: number
  ): Promise<CommentResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}/comments`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ body, parent_id: parentId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to add comment' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Delete a comment.
   */
  deleteScorecardComment: async (
    scorecardId: number,
    commentId: number,
    token: string
  ): Promise<{ status: string }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/${scorecardId}/comments/${commentId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to delete comment');
    return response.json();
  },

  /**
   * Report a comment.
   */
  reportScorecardComment: async (
    scorecardId: number,
    commentId: number,
    reason: 'spam' | 'harassment' | 'inappropriate' | 'other',
    token: string,
    details?: string
  ): Promise<{ status: string; message: string }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/${scorecardId}/comments/${commentId}/report`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason, details }),
      }
    );
    if (!response.ok) throw new Error('Failed to report comment');
    return response.json();
  },

  // ==========================================================================
  // Admin Scorecard Endpoints
  // ==========================================================================

  /**
   * Get moderation queue (admin only).
   */
  getModerationQueue: async (
    token: string,
    params: { status?: string; limit?: number; offset?: number } = {}
  ): Promise<ModerationQueueResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/moderation-queue${queryString}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch moderation queue');
    return response.json();
  },

  /**
   * Approve a scorecard (admin only).
   */
  approveScorecard: async (
    scorecardId: number,
    token: string
  ): Promise<{ status: string; scorecard_id: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/${scorecardId}/approve`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to approve scorecard');
    return response.json();
  },

  /**
   * Reject a scorecard (admin only).
   */
  rejectScorecard: async (
    scorecardId: number,
    reason: string,
    token: string
  ): Promise<{ status: string; scorecard_id: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/${scorecardId}/reject`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      }
    );
    if (!response.ok) throw new Error('Failed to reject scorecard');
    return response.json();
  },

  /**
   * Remove featured status from a scorecard (admin only).
   */
  unfeatureScorecard: async (
    scorecardId: number,
    token: string
  ): Promise<{ scorecard_id: number; is_featured: boolean }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/${scorecardId}/feature`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to unfeature scorecard');
    return response.json();
  },

  /**
   * Feature a scorecard with optional editorial blurb (admin only).
   */
  featureWithEditorial: async (
    scorecardId: number,
    data: {
      editorial: string | null;
      template_style?: string;
      editor_caption?: string | null;
      combo_layout?: string;
    },
    token: string
  ): Promise<{ scorecard_id: number; is_featured: boolean; featured_editorial: string | null }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/${scorecardId}/feature`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) throw new Error('Failed to feature scorecard');
    return response.json();
  },

  /**
   * Get archive spotlight week (admin only).
   */
  getSpotlightWeek: async (
    weekStart: string,
    token: string
  ): Promise<ArchiveSpotlightWeekResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/spotlights/week?week_start=${weekStart}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch spotlight week');
    return response.json();
  },

  /**
   * Create archive spotlight (admin only).
   */
  createSpotlight: async (
    data: {
      spotlight_date: string;
      scorecard_id: number;
      editorial?: string;
      spotlight_type?: string;
      description?: string;
    },
    token: string
  ): Promise<ArchiveSpotlightItem> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/spotlights`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create spotlight');
    return response.json();
  },

  /**
   * Update archive spotlight (admin only).
   */
  updateSpotlight: async (
    spotlightId: number,
    data: {
      editorial?: string;
      scorecard_id?: number;
      spotlight_type?: string;
      description?: string;
    },
    token: string
  ): Promise<ArchiveSpotlightItem> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/spotlights/${spotlightId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update spotlight');
    return response.json();
  },

  /**
   * Delete archive spotlight (admin only).
   */
  deleteSpotlight: async (spotlightId: number, token: string): Promise<void> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/spotlights/${spotlightId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to delete spotlight');
  },

  /**
   * Get today's spotlight scorecard (public).
   */
  getTodaySpotlight: async (): Promise<ArchiveSpotlightItem | null> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/spotlights/today`);
    if (!response.ok) throw new Error('Failed to fetch today spotlight');
    return response.json();
  },

  /**
   * Get unified editorial zone data (Editor's Pick + Spotlight).
   */
  getEditorialZone: async (): Promise<EditorialZoneResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/editorial-zone`);
    if (!response.ok) throw new Error('Failed to fetch editorial zone');
    return response.json();
  },

  /**
   * Get auto-suggested games for the Spotlight (admin only).
   */
  getSpotlightSuggestions: async (
    token: string,
    targetDate?: string,
    limit?: number
  ): Promise<SpotlightSuggestionsResponse> => {
    const params = new URLSearchParams();
    if (targetDate) params.append('target_date', targetDate);
    if (limit) params.append('limit', limit.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/spotlights/suggestions${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch spotlight suggestions');
    return response.json();
  },

  /**
   * Get recent scorecards for games with matching moment tags (admin only).
   */
  getRecentMomentUploads: async (
    token: string,
    category: 'milestone' | 'non_milestone' = 'non_milestone',
    limit: number = 20
  ): Promise<MomentUploadsResponse> => {
    const params = new URLSearchParams({ moment_category: category, limit: limit.toString() });
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/moments/recent-uploads?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch recent moment uploads');
    return response.json();
  },

  /**
   * Get flagged comments (admin only).
   */
  getFlaggedComments: async (
    token: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<FlaggedCommentsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/flagged-comments${queryString}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch flagged comments');
    return response.json();
  },

  /**
   * Moderate a comment (admin only).
   */
  moderateComment: async (
    commentId: number,
    action: 'approve' | 'hide' | 'delete',
    token: string
  ): Promise<{ status: string; comment_id: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/scorecards/comments/${commentId}/moderate`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      }
    );
    if (!response.ok) throw new Error('Failed to moderate comment');
    return response.json();
  },

  /**
   * Get moderation history grouped by user (admin only).
   */
  getModerationHistory: async (token: string): Promise<ModerationHistoryResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/scorecards/moderation-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch moderation history');
    return response.json();
  },

  // ==========================================================================
  // Scorecard Pages Endpoints
  // ==========================================================================

  /**
   * Get current user's scorecard profile with stats (My Scorecards page).
   */
  getMyScorecardsProfile: async (
    token: string,
    filters: ScorecardProfileFilters = {}
  ): Promise<UserScorecardProfileResponse> => {
    const qs = buildProfileFilterQuery(filters);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/my${qs ? `?${qs}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch my scorecards profile');
    return response.json();
  },

  /**
   * Get a user's public scorecard profile.
   */
  getUserScorecardProfile: async (
    username: string,
    params: ScorecardProfileFilters = {},
    token?: string
  ): Promise<UserScorecardProfileResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const qs = buildProfileFilterQuery(params);

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/user/${encodeURIComponent(username)}${qs ? `?${qs}` : ''}`,
      { headers }
    );
    if (response.status === 403) {
      throw new Error('profile_private');
    }
    if (!response.ok) throw new Error('Failed to fetch user profile');
    return response.json();
  },

  /**
   * Typeahead search for players who appear in the current user's scorecards.
   */
  searchMyScorecardPlayers: async (
    token: string,
    q: string,
    limit: number = 10
  ): Promise<PlayerSearchResponse> => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/my/players/search?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to search players');
    return response.json();
  },

  /**
   * Get full stat list for a user (expanded from top 6).
   */
  getUserFullStatList: async (
    username: string,
    statType: string,
    token?: string
  ): Promise<FullStatListResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/user/${encodeURIComponent(username)}/stats/${statType}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch stat list');
    return response.json();
  },

  /**
   * Get latest public scorecards.
   */
  getLatestScorecards: async (
    limit: number = 20,
    token?: string
  ): Promise<LatestScorecardsResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/latest?limit=${limit}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch latest scorecards');
    return response.json();
  },

  /**
   * Get top uploaders all-time.
   */
  getTopUploaders: async (limit: number = 10): Promise<TopUploadersResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/stats/top-uploaders?limit=${limit}`
    );
    if (!response.ok) throw new Error('Failed to fetch top uploaders');
    return response.json();
  },

  /**
   * Get top uploaders current season.
   */
  getTopUploadersCurrentSeason: async (limit: number = 10): Promise<TopUploadersResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/stats/top-uploaders-season?limit=${limit}`
    );
    if (!response.ok) throw new Error('Failed to fetch top uploaders');
    return response.json();
  },

  /**
   * Get most scored games.
   */
  getMostScoredGames: async (limit: number = 20): Promise<MostScoredGamesResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/stats/most-scored-games?limit=${limit}`
    );
    if (!response.ok) throw new Error('Failed to fetch most scored games');
    return response.json();
  },

  /**
   * Get season summary grid (1901-2030).
   */
  getSeasonSummary: async (): Promise<SeasonSummaryResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecard-pages/seasons/summary`);
    if (!response.ok) throw new Error('Failed to fetch season summary');
    return response.json();
  },

  /**
   * Get scorecards for a specific season.
   */
  getSeasonScorecards: async (
    year: number,
    params: { limit?: number; offset?: number } = {}
  ): Promise<SeasonDetailResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/seasons/${year}${queryString}`
    );
    if (!response.ok) throw new Error('Failed to fetch season scorecards');
    return response.json();
  },

  /**
   * Get comprehensive scorecard statistics overview.
   * Includes hero metrics, distributions, leaderboards, and fun facts.
   */
  getScorecardStatsOverview: async (): Promise<ScorecardStatsResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecard-pages/stats/overview`);
    if (!response.ok) throw new Error('Failed to fetch scorecard stats');
    return response.json();
  },

  // ==========================================================================
  // Archive — Browse/Search
  // ==========================================================================

  getArchiveScorecards: async (
    params: ArchiveScorecardsParams = {}
  ): Promise<ArchiveScorecardsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.team) searchParams.append('team', params.team);
    if (params.season) searchParams.append('season', params.season);
    if (params.game_type) searchParams.append('game_type', params.game_type);
    if (params.scorer) searchParams.append('scorer', params.scorer);
    if (params.game_pk) searchParams.append('game_pk', params.game_pk);
    if (params.moment_type) searchParams.append('moment_type', params.moment_type);
    if (params.month) searchParams.append('month', params.month);
    if (params.day) searchParams.append('day', params.day);
    if (params.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-pages/archive/scorecards${qs}`
    );
    if (!response.ok) throw new Error('Failed to fetch archive scorecards');
    return response.json();
  },

  getArchiveTeams: async (params: ArchiveTeamsParams = {}): Promise<ArchiveTeamsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.league) searchParams.append('league', params.league);
    if (params.division) searchParams.append('division', params.division);
    if (params.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecard-pages/archive/teams${qs}`);
    if (!response.ok) throw new Error('Failed to fetch archive teams');
    return response.json();
  },

  getArchiveGames: async (params: ArchiveGamesParams = {}): Promise<ArchiveGamesResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.team) searchParams.append('team', params.team);
    if (params.season) searchParams.append('season', params.season);
    if (params.game_type) searchParams.append('game_type', params.game_type);
    if (params.venue) searchParams.append('venue', params.venue);
    if (params.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecard-pages/archive/games${qs}`);
    if (!response.ok) throw new Error('Failed to fetch archive games');
    return response.json();
  },

  getArchiveScorers: async (params: ArchiveScorersParams = {}): Promise<ArchiveScorersResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.team) searchParams.append('team', params.team);
    if (params.fav_team) searchParams.append('fav_team', params.fav_team);
    if (params.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecard-pages/archive/scorers${qs}`);
    if (!response.ok) throw new Error('Failed to fetch archive scorers');
    return response.json();
  },

  // ==========================================================================
  // Admin - Feature Flags
  // ==========================================================================

  /**
   * Get all feature flags (admin only).
   */
  getAdminFlags: async (token: string): Promise<FlagResponse[]> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/flags`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch flags');
    return response.json();
  },

  /**
   * Update a feature flag (admin only).
   */
  updateFeatureFlag: async (
    key: string,
    enabled: boolean,
    token: string
  ): Promise<FlagResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/flags/${key}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ enabled }),
    });
    if (!response.ok) throw new Error('Failed to update flag');
    return response.json();
  },

  // ==========================================================================
  // Admin - API Documentation
  // ==========================================================================

  /**
   * Get structured API documentation (admin only).
   */
  getApiDocs: async (token: string, includeAdmin: boolean = true) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/api-docs?include_admin=${includeAdmin}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch API docs');
    return response.json();
  },

  // ==========================================================================
  // Admin - Health Dashboard
  // ==========================================================================

  /**
   * Get full health report (admin only).
   */
  getHealthReport: async (token: string) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/health`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch health report');
    return response.json();
  },

  // ==========================================================================
  // Follow Endpoints
  // ==========================================================================

  /**
   * Follow a user.
   */
  followUser: async (userId: number, token: string): Promise<FollowToggleResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/${userId}/follow`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to follow user' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Unfollow a user.
   */
  unfollowUser: async (userId: number, token: string): Promise<FollowToggleResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/${userId}/follow`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to unfollow user');
    return response.json();
  },

  /**
   * Get a user's followers.
   */
  getFollowers: async (
    userId: number,
    params: { limit?: number; offset?: number } = {},
    token?: string
  ): Promise<FollowListResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/users/${userId}/followers${queryString}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch followers');
    return response.json();
  },

  /**
   * Get users that a user is following.
   */
  getFollowing: async (
    userId: number,
    params: { limit?: number; offset?: number } = {},
    token?: string
  ): Promise<FollowListResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/users/${userId}/following${queryString}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch following');
    return response.json();
  },

  /**
   * Check if current user is following a target user.
   */
  getFollowStatus: async (userId: number, token: string): Promise<FollowStatusResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/${userId}/follow/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to check follow status');
    return response.json();
  },

  /**
   * Get feed of scorecards from followed users.
   */
  getFollowingFeed: async (
    params: { limit?: number; offset?: number } = {},
    token: string
  ): Promise<FollowingFeedResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(
      `${API_BASE_URL}/users/me/following/feed${queryString}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch following feed');
    return response.json();
  },

  // ==========================================================================
  // Reaction Endpoints
  // ==========================================================================

  /**
   * Get all active reaction types.
   */
  getReactionTypes: async (): Promise<ReactionTypesListResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/reactions/types`);
    if (!response.ok) throw new Error('Failed to fetch reaction types');
    return response.json();
  },

  /**
   * Get reactions on a scorecard.
   */
  getScorecardReactions: async (
    scorecardId: number,
    token?: string
  ): Promise<ScorecardReactionsResponse> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/${scorecardId}/reactions`, {
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch scorecard reactions');
    return response.json();
  },

  /**
   * Toggle a reaction on a scorecard.
   */
  toggleReaction: async (
    scorecardId: number,
    reactionCode: string,
    token: string
  ): Promise<ReactionToggleResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecards/${scorecardId}/reactions/${reactionCode}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to toggle reaction');
    return response.json();
  },

  // ==========================================================================
  // Admin Reaction Endpoints
  // ==========================================================================

  /**
   * Get all reaction types (admin view with usage stats).
   */
  adminGetReactionTypes: async (token: string): Promise<ReactionTypesAdminListResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch reaction types');
    return response.json();
  },

  /**
   * Create a new reaction type (admin only).
   */
  adminCreateReactionType: async (
    data: ReactionTypeCreateRequest,
    token: string
  ): Promise<ReactionTypeAdminResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'Failed to create reaction type' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Update a reaction type (admin only).
   */
  adminUpdateReactionType: async (
    id: number,
    data: ReactionTypeUpdateRequest,
    token: string
  ): Promise<ReactionTypeAdminResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update reaction type');
    return response.json();
  },

  /**
   * Delete a reaction type (admin only).
   */
  adminDeleteReactionType: async (
    id: number,
    token: string
  ): Promise<ReactionTypeDeleteResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'Failed to delete reaction type' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Toggle active status of a reaction type (admin only).
   */
  adminToggleReactionType: async (
    id: number,
    token: string
  ): Promise<ReactionTypeToggleResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types/${id}/toggle`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to toggle reaction type');
    return response.json();
  },

  /**
   * Reorder reaction types (admin only).
   */
  adminReorderReactionTypes: async (
    order: number[],
    token: string
  ): Promise<{ status: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/reactions/types/reorder`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ order }),
    });
    if (!response.ok) throw new Error('Failed to reorder reaction types');
    return response.json();
  },

  // ==========================================================================
  // Scoring Defaults & Onboarding Endpoints
  // ==========================================================================

  /**
   * Get current user's scoring defaults.
   */
  getScoringDefaults: async (token: string): Promise<ScoringDefaultsResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/scoring-defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch scoring defaults');
    return response.json();
  },

  /**
   * Update current user's scoring defaults.
   */
  updateScoringDefaults: async (
    data: Partial<ScoringDefaultsData>,
    token: string
  ): Promise<ScoringDefaultsResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/scoring-defaults`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update scoring defaults');
    return response.json();
  },

  /**
   * Complete onboarding with optional scoring defaults.
   */
  completeOnboarding: async (
    data: Partial<ScoringDefaultsData>,
    token: string
  ): Promise<OnboardingCompleteResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/onboarding`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to complete onboarding');
    return response.json();
  },

  /**
   * Skip onboarding.
   */
  skipOnboarding: async (token: string): Promise<OnboardingCompleteResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/onboarding/skip`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to skip onboarding');
    return response.json();
  },

  /**
   * Get onboarding status.
   */
  getOnboardingStatus: async (token: string): Promise<OnboardingStatusResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/onboarding/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch onboarding status');
    return response.json();
  },

  // ==========================================================================
  // Liked Scorecards Endpoints
  // ==========================================================================

  /**
   * Get scorecards liked by the current user.
   */
  getLikedScorecards: async (
    params: { limit?: number; offset?: number } = {},
    token: string
  ): Promise<LikedScorecardsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetchWithTimeout(`${API_BASE_URL}/scorecards/liked${queryString}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch liked scorecards');
    return response.json();
  },

  // ==========================================================================
  // Analytics (User Journey Tracking)
  // ==========================================================================

  /**
   * Track an analytics event (fire-and-forget).
   * Works for both authenticated and anonymous users.
   */
  trackAnalyticsEvent: async (
    event: {
      event_type: string;
      event_subtype?: string;
      session_id: string;
      page_path?: string;
      referrer_path?: string;
      target_type?: string;
      target_id?: number;
      event_data?: Record<string, unknown>;
      device_type?: string;
    },
    token?: string
  ): Promise<{ status: string; event_id: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/analytics/track`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(event),
      },
      5000 // Short timeout for tracking
    );
    if (!response.ok) throw new Error('Failed to track event');
    return response.json();
  },

  /**
   * Get analytics overview (super-admin only).
   */
  getAnalyticsOverview: async (token: string): Promise<AnalyticsOverviewResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/analytics/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch analytics overview');
    return response.json();
  },

  // ==========================================================================
  // Contact Form
  // ==========================================================================

  submitContactForm: async (data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<{ id: number; status: string; created_at: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to submit form' }));
      throw new Error(error.detail || 'Failed to submit contact form');
    }
    return response.json();
  },

  // ==========================================================================
  // Feedback System
  // ==========================================================================

  submitFeedback: async (data: FeedbackSubmission): Promise<FeedbackResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to submit feedback: ${response.status}`);
    }
    return response.json();
  },

  // ==========================================================================
  // Donations / Tip Jar endpoints
  // ==========================================================================

  getDonationGoal: async (): Promise<GoalResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/goal`);
    if (!response.ok) throw new Error('Failed to fetch donation goal');
    return response.json();
  },

  createPaymentIntent: async (
    data: CreatePaymentIntentRequest,
    token?: string
  ): Promise<PaymentIntentResponse> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/create-payment-intent`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Payment failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  createSubscription: async (
    data: CreateSubscriptionRequest,
    token: string
  ): Promise<SubscriptionResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/create-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Subscription failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  getMySubscriptions: async (token: string): Promise<SubscriptionItem[]> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/my-subscriptions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch subscriptions');
    return response.json();
  },

  getMyDonationHistory: async (token: string): Promise<DonationHistoryItem[]> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/my-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch donation history');
    return response.json();
  },

  createCustomerPortal: async (
    returnUrl: string,
    token: string
  ): Promise<CustomerPortalResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/donations/customer-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ return_url: returnUrl }),
    });
    if (!response.ok) throw new Error('Failed to create portal session');
    return response.json();
  },

  // ==========================================================================
  // Badge API
  // ==========================================================================

  getBadgeCatalog: async (): Promise<BadgeCatalogResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/badges`);
    if (!response.ok) throw new Error('Failed to fetch badge catalog');
    return response.json();
  },

  getUserBadges: async (userId: number): Promise<UserBadgesPublicResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/${userId}/badges`);
    if (!response.ok) throw new Error('Failed to fetch user badges');
    return response.json();
  },

  getMyBadges: async (token: string): Promise<UserBadgesDashboardResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/badges`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch badge dashboard');
    return response.json();
  },

  getBadgeNotifications: async (token: string): Promise<BadgeNotificationsListResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/badge-notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch badge notifications');
    return response.json();
  },

  markBadgeNotificationsRead: async (token: string): Promise<{ status: string; count: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/me/badge-notifications/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to mark notifications read');
    return response.json();
  },

  // Notifications
  getNotifications: async (
    token: string,
    limit = 30,
    offset = 0
  ): Promise<NotificationListResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/notifications?limit=${limit}&offset=${offset}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },

  getUnreadNotificationCount: async (token: string): Promise<{ count: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/notifications/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch unread count');
    return response.json();
  },

  markAllNotificationsRead: async (token: string): Promise<{ status: string; count: number }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/notifications/mark-all-read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to mark notifications read');
    return response.json();
  },

  markNotificationRead: async (
    token: string,
    notificationId: number
  ): Promise<{ status: string; count: number }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/notifications/${notificationId}/read`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Failed to mark notification read');
    return response.json();
  },

  // Push Notifications
  getVapidKey: async (token: string): Promise<{ public_key: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/push/vapid-key`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch VAPID key');
    return response.json();
  },

  subscribePush: async (
    token: string,
    subscription: PushSubscriptionJSON,
    userAgent?: string
  ): Promise<{ status: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/push/subscribe`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        user_agent: userAgent,
      }),
    });
    if (!response.ok) throw new Error('Failed to subscribe to push');
    return response.json();
  },

  unsubscribePush: async (token: string, endpoint: string): Promise<{ status: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/push/unsubscribe`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ endpoint }),
    });
    if (!response.ok) throw new Error('Failed to unsubscribe from push');
    return response.json();
  },

  getPushStatus: async (token: string): Promise<PushStatusResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/push/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch push status');
    return response.json();
  },

  grantBadge: async (
    body: { user_id: number; badge_key: string; context?: Record<string, unknown> },
    token: string
  ): Promise<{ status: string; message: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/badges/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Grant failed' }));
      throw new Error(err.detail || 'Failed to grant badge');
    }
    return response.json();
  },

  revokeBadge: async (
    body: { user_id: number; badge_key: string; season?: number },
    token: string
  ): Promise<{ status: string; message: string }> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/badges/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Revoke failed' }));
      throw new Error(err.detail || 'Failed to revoke badge');
    }
    return response.json();
  },

  // ==========================================================================
  // Editor's Pick Queue (admin)
  // ==========================================================================

  getEditorPickQueue: async (token: string, days: number = 3): Promise<EditorPickQueueResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/editor-picks/queue?days=${days}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch editor pick queue');
    return response.json();
  },

  scheduleEditorPick: async (
    body: {
      scheduled_date: string;
      scorecard_id: number;
      editorial?: string | null;
      editor_caption?: string | null;
      template_style?: string | null;
      combo_layout?: string | null;
    },
    token: string
  ): Promise<EditorPickQueueItem> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/editor-picks/queue`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Schedule failed' }));
      throw new Error(err.detail || 'Failed to schedule editor pick');
    }
    return response.json();
  },

  updateEditorPick: async (
    pickId: number,
    body: {
      editorial?: string | null;
      editor_caption?: string | null;
      template_style?: string | null;
      combo_layout?: string | null;
      scorecard_id?: number | null;
    },
    token: string
  ): Promise<EditorPickQueueItem> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/editor-picks/queue/${pickId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Update failed' }));
      throw new Error(err.detail || 'Failed to update editor pick');
    }
    return response.json();
  },

  deleteEditorPick: async (pickId: number, token: string): Promise<void> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/admin/editor-picks/queue/${pickId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Delete failed' }));
      throw new Error(err.detail || 'Failed to delete editor pick');
    }
  },

  publishEditorPick: async (
    pickId: number,
    token: string
  ): Promise<{ pick_id: number; scorecard_id: number; status: string }> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/editor-picks/queue/${pickId}/publish`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Publish failed' }));
      throw new Error(err.detail || 'Failed to publish editor pick');
    }
    return response.json();
  },

  getEditorPickSuggestions: async (
    token: string,
    limit: number = 10
  ): Promise<EditorPickSuggestionsResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/admin/editor-picks/suggestions?limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to fetch editor pick suggestions');
    return response.json();
  },

  // Render a scorecard template pre-filled with game data. Requires auth for
  // all template keys (see TemplateKey) — the backend applies
  // require_current_user_id uniformly, no template is served unauthenticated.
  renderScorecardTemplate: async (
    templateKey: string,
    gamePk: number,
    token: string
  ): Promise<Blob> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/scorecard-templates/render/${encodeURIComponent(templateKey)}?game_pk=${gamePk}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to render scorecard template');
    return response.blob();
  },
};

// ==========================================================================
// Push Notification Types
// ==========================================================================

export interface PushStatusResponse {
  enabled: boolean;
  subscription_count: number;
  push_supported: boolean;
}

// ==========================================================================
// Scorecard Types
// ==========================================================================

export interface UserSummary {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface GameSummary {
  id: number;
  game_pk?: string | null;
  date: string;
  home_team_code: string;
  away_team_code: string;
  home_score: number | null;
  away_score: number | null;
}

export interface NewlyEarnedBadge {
  badge_key: string;
  name: string;
  description: string;
  level: number;
  emoji?: string | null;
  svg_key?: string | null;
  earned_at: string;
}

export interface ScorecardUploadResponse {
  status: string;
  scorecard_id: number | null;
  moderation_status: string | null;
  message: string;
  existing_scorecard_id: number | null;
  newly_earned_badges: NewlyEarnedBadge[];
}

export interface DuplicateCheckResponse {
  is_duplicate: boolean;
  existing_scorecard_id: number | null;
}

export interface ExistingScorecardCheckResponse {
  has_existing: boolean;
  existing_scorecard_id: number | null;
  existing_thumbnail_url: string | null;
  existing_created_at: string | null;
}

export interface ScorecardPageResponse {
  page_number: number;
  image_url: string;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
}

export interface ScoringMetadata {
  viewing_method: string | null;
  scoring_tool: string | null;
  game_prep: string | null;
  scoring_style: string | null;
  scorebook_type: string | null;
  scorebook_subtype: string | null;
  game_outcome: string | null;
  mvp_player_mlb_id: number | null;
  mvp_player_name: string | null;
}

export interface ScorecardResponse {
  id: number;
  thumbnail_url: string | null;
  original_url: string | null;
  page_count: number;
  is_public: boolean;
  attended_in_person: boolean;
  caption: string | null;
  like_count: number;
  comment_count: number;
  is_featured: boolean;
  created_at: string;
  user: UserSummary | null;
  is_mine: boolean;
  is_liked: boolean;
  mvp_player_mlb_id: number | null;
  mvp_player_name: string | null;
  scoring_metadata: ScoringMetadata | null;
}

export interface ScorecardDetailResponse extends ScorecardResponse {
  game: GameSummary;
  moderation_status: string;
  pages: ScorecardPageResponse[];
}

export interface ScorecardListResponse {
  scorecards: ScorecardResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FeaturedScorecardResponse {
  id: number;
  thumbnail_url: string | null;
  caption: string | null;
  attended_in_person: boolean;
  featured_at: string | null;
  featured_editorial: string | null;
  like_count: number;
  user: UserSummary | null;
  game: GameSummary;
}

export interface FeaturedListResponse {
  scorecards: FeaturedScorecardResponse[];
}

export interface ArchiveSpotlightItem {
  id: number;
  spotlight_date: string;
  scorecard_id: number;
  editorial: string | null;
  spotlight_type: string;
  description: string | null;
  status: string;
  scorecard: FeaturedScorecardResponse;
  created_at: string;
}

export interface ArchiveSpotlightWeekResponse {
  spotlights: ArchiveSpotlightItem[];
  week_start: string;
  week_end: string;
}

export interface CommentResponse {
  id: number;
  body: string;
  parent_id: number | null;
  created_at: string;
  user: UserSummary | null;
  status?: string;
  message?: string;
}

export interface CommentListResponse {
  comments: CommentResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ModerationQueueItem {
  id: number;
  thumbnail_url: string | null;
  original_url: string | null;
  auto_moderation_score: number | null;
  auto_moderation_labels: unknown[] | null;
  created_at: string;
  user: UserSummary | null;
  game_date: string;
  matchup: string;
}

export interface ModerationQueueResponse {
  scorecards: ModerationQueueItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface FlaggedCommentItem {
  id: number;
  body: string;
  toxicity_score: number | null;
  report_count: number;
  created_at: string;
  user: UserSummary | null;
  scorecard_id: number;
}

export interface FlaggedCommentsResponse {
  comments: FlaggedCommentItem[];
}

export interface ModerationHistoryItem {
  id: number;
  body: string;
  toxicity_score: number | null;
  report_count: number;
  moderation_status: string;
  created_at: string;
  scorecard_id: number;
}

export interface ModerationHistoryUser {
  user_id: number;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  flagged_count: number;
  comments: ModerationHistoryItem[];
}

export interface ModerationHistoryResponse {
  users: ModerationHistoryUser[];
  total_flagged: number;
}

// ==========================================================================
// Scorecard Pages Types
// ==========================================================================

export interface ScorecardPageUserSummary {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_own_profile: boolean;
}

export interface TeamWinLoss {
  w: number;
  l: number;
}

export interface UserProfileStats {
  total_scorecards: number;
  total_in_person: number;
  by_season: Record<string, number>;
  by_team: Record<string, number>;
  team_win_loss: Record<string, TeamWinLoss>;
  top_hr_hitters: Array<Record<string, unknown>>;
  top_rbi_hitters: Array<Record<string, unknown>>;
  most_selected_mvps: Array<Record<string, unknown>>;
  top_wins_pitchers: Array<Record<string, unknown>>;
}

export interface RecentScorecardItem {
  id: number;
  thumbnail_url: string | null;
  game_date: string | null;
  winner: string;
  loser: string;
  score: string;
  attended_in_person: boolean;
  is_public: boolean;
  moderation_status: string | null;
  uploaded_at: string | null;
  comment_count: number;
  // Enrichment indicators for nudge UI
  caption: string | null;
  has_mvp: boolean;
  has_scoring_details: boolean;
}

export interface CompetitionOption {
  key: string;
  label: string;
}

export interface ProfileFilterOptions {
  seasons: number[];
  competitions: CompetitionOption[];
}

export interface PlayerSearchItem {
  mlb_id: number;
  name: string;
}

export interface PlayerSearchResponse {
  results: PlayerSearchItem[];
}

export interface AppliedProfileFilters {
  seasons: number[];
  competitions: string[];
  teams: string[];
  game_types: string[];
  players: PlayerSearchItem[];
  sort: string | null;
}

export interface UserScorecardProfileResponse {
  user: ScorecardPageUserSummary;
  stats: UserProfileStats;
  recent_scorecards: RecentScorecardItem[];
  page: number;
  per_page: number;
  total_scorecards_count: number;
  filter_options: ProfileFilterOptions;
  applied_filters: AppliedProfileFilters;
}

export interface ScorecardProfileFilters {
  /** CSV of season years, e.g. "2025" or "2025,2026" */
  season?: string | null;
  /** CSV of competition keys, e.g. "mlb" or "mlb,wbc" */
  competition?: string | null;
  /** CSV of team codes, e.g. "PHI" or "PHI,NYM" */
  team?: string | null;
  /** CSV of game-type keys, e.g. "regular" or "regular,postseason" */
  game_type?: string | null;
  /** CSV of MLB player IDs */
  player?: string | null;
  /** Sort key for recent_scorecards */
  sort?: string | null;
  page?: number;
  per_page?: number;
}

export interface LatestScorecardItem {
  id: number;
  thumbnail_url: string | null;
  game_date: string | null;
  winner: string;
  loser: string;
  score: string;
  uploaded_by: string;
  username: string | null;
  user_id: number | null;
  avatar_url: string | null;
  uploaded_at: string | null;
  like_count: number;
  comment_count: number;
  reaction_count: number;
  is_liked: boolean;
  is_mine: boolean;
  is_featured: boolean;
  attended_in_person: boolean;
  caption: string | null;
}

export interface LatestScorecardsResponse {
  scorecards: LatestScorecardItem[];
}

export interface TopUploaderItem {
  user_id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  scorecard_count: number;
  most_scored_team: string | null;
  season?: number;
}

export interface TopUploadersResponse {
  uploaders: TopUploaderItem[];
}

export interface MostScoredGameItem {
  game_id: string;
  game_date: string | null;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  scorecard_count: number;
}

export interface MostScoredGamesResponse {
  games: MostScoredGameItem[];
}

export interface SeasonSummaryItem {
  count: number;
  games: number;
  has_scorecards: boolean;
}

export interface SeasonSummaryResponse {
  seasons: Record<number, SeasonSummaryItem>;
}

export interface GameWithScorecardsItem {
  game_id: string;
  game_date: string | null;
  winner: string;
  loser: string;
  score: string;
  scorecard_count: number;
  thumbnails: string[];
}

export interface SeasonDetailResponse {
  season: number;
  games: GameWithScorecardsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface FullStatListResponse {
  stat_type: string;
  data: unknown;
}

// ==========================================================================
// Archive Types
// ==========================================================================

export interface ArchiveScorecardItem {
  id: number;
  thumbnail_url: string | null;
  game_date: string | null;
  winner: string;
  loser: string;
  score: string;
  uploaded_by: string;
  username: string | null;
  user_id: number | null;
  avatar_url: string | null;
  uploaded_at: string | null;
  like_count: number;
  comment_count: number;
  attended_in_person: boolean;
  is_featured: boolean;
  caption: string | null;
  game_pk: string | null;
  pages?: Array<{ width?: number; height?: number }>;
  page_count?: number;
}

export interface ArchiveScorecardsResponse {
  scorecards: ArchiveScorecardItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ArchiveTeamItem {
  code: string;
  name: string;
  league: string;
  color: string;
  scorecard_count: number;
  scorer_count: number;
}

export interface ArchiveTeamsResponse {
  teams: ArchiveTeamItem[];
}

export interface ArchiveGameItem {
  game_pk: string;
  game_date: string | null;
  matchup: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  venue: string | null;
  scorecard_count: number;
  is_postseason: boolean;
  series_description: string | null;
  month_group: string;
}

export interface ArchiveGamesResponse {
  games: ArchiveGameItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ArchiveScorerItem {
  user_id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  scorecard_count: number;
  current_season_count: number;
  best_season: string | null;
  games_attended: number;
  like_count: number;
  favorite_team: string | null;
  teams: string[];
  member_since: string | null;
}

export interface ArchiveScorersResponse {
  scorers: ArchiveScorerItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ArchiveScorecardsParams {
  page?: number;
  per_page?: number;
  search?: string;
  team?: string;
  season?: string;
  game_type?: string;
  scorer?: string;
  game_pk?: string;
  moment_type?: string;
  /** Comma-separated game months, 1-12 (e.g. "4,5"). */
  month?: string;
  /** Comma-separated game day-of-month values, 1-31. */
  day?: string;
  sort?: string;
}

export interface ArchiveTeamsParams {
  league?: string;
  division?: string;
  sort?: string;
}

export interface ArchiveGamesParams {
  page?: number;
  per_page?: number;
  team?: string;
  season?: string;
  game_type?: string;
  venue?: string;
  sort?: string;
}

export interface ArchiveScorersParams {
  page?: number;
  per_page?: number;
  team?: string;
  fav_team?: string;
  sort?: string;
}

// ==========================================================================
// Feature Flag Types
// ==========================================================================

export interface FlagResponse {
  id: number;
  key: string;
  enabled: boolean;
  description: string | null;
  category: string;
  scope: string;
  updated_at: string | null;
}

// ==========================================================================
// Follow Types
// ==========================================================================

export interface FollowToggleResponse {
  action: 'followed' | 'unfollowed';
  follower_count: number;
  following_count: number;
}

export interface FollowUserResponse {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  scorecard_count: number;
  follower_count: number;
  is_following: boolean;
  followed_at: string;
}

export interface FollowListResponse {
  users: FollowUserResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FollowStatusResponse {
  is_following: boolean;
}

// ==========================================================================
// Reaction Types
// ==========================================================================

export interface ReactionTypeResponse {
  id: number;
  code: string;
  emoji: string;
  label: string;
  description: string | null;
}

export interface ReactionTypeAdminResponse extends ReactionTypeResponse {
  is_active: boolean;
  sort_order: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface ReactionTypesListResponse {
  reaction_types: ReactionTypeResponse[];
}

export interface ReactionTypesAdminListResponse {
  reaction_types: ReactionTypeAdminResponse[];
}

export interface ReactionCount {
  code: string;
  emoji: string;
  count: number;
}

export interface ScorecardReactionsResponse {
  reactions: ReactionCount[];
  user_reactions: string[];
  total_count: number;
}

export interface ReactionToggleResponse {
  action: 'added' | 'removed';
  reactions: ReactionCount[];
  user_reactions: string[];
}

export interface ReactionTypeCreateRequest {
  code: string;
  emoji: string;
  label: string;
  description?: string;
  is_active?: boolean;
}

export interface ReactionTypeUpdateRequest {
  emoji?: string;
  label?: string;
  description?: string;
  is_active?: boolean;
}

export interface ReactionTypeToggleResponse {
  id: number;
  code: string;
  is_active: boolean;
}

export interface ReactionTypeDeleteResponse {
  deleted: boolean;
  id: number;
}

// ==========================================================================
// Scoring Defaults Types
// ==========================================================================

export interface ScoringDefaultsData {
  viewing_method: string | null;
  scoring_tool: string | null;
  game_prep: string | null;
  scoring_style: string | null;
  scorebook_type: string | null;
  scorebook_subtype: string | null;
}

export interface ScoringDefaultsResponse {
  has_defaults: boolean;
  defaults: ScoringDefaultsData | null;
}

export interface OnboardingCompleteResponse {
  status: string;
  has_completed_onboarding: boolean;
  message: string;
}

export interface OnboardingStatusResponse {
  has_completed_onboarding: boolean;
  has_scoring_defaults: boolean;
}

// ==========================================================================
// Following Feed Types
// ==========================================================================

export interface FollowingFeedItem {
  id: number;
  thumbnail_url: string | null;
  caption: string | null;
  attended_in_person: boolean;
  like_count: number;
  comment_count: number;
  reaction_count: number;
  created_at: string;
  user: UserSummary;
  game: GameSummary;
}

export interface FollowingFeedResponse {
  scorecards: FollowingFeedItem[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Liked Scorecards Types
// ==========================================================================

export interface LikedScorecardItem {
  id: number;
  thumbnail_url: string | null;
  caption: string | null;
  attended_in_person: boolean;
  like_count: number;
  comment_count: number;
  liked_at: string;
  created_at: string;
  user: UserSummary | null;
  game: GameSummary;
}

export interface LikedScorecardsResponse {
  scorecards: LikedScorecardItem[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Badge Types
// ==========================================================================

export interface BadgeDefinitionItem {
  badge_key: string;
  name: string;
  description: string;
  category: string;
  level: number;
  emoji?: string | null;
  svg_key?: string | null;
  success_metric: string;
  is_repeatable: boolean;
  sort_order: number;
}

export interface BadgeCatalogResponse {
  badges: BadgeDefinitionItem[];
  total: number;
}

export interface EarnedBadge {
  badge_key: string;
  name: string;
  description: string;
  level: number;
  emoji?: string | null;
  svg_key?: string | null;
  category: string;
  earned_at: string;
  season?: number | null;
  context?: Record<string, unknown> | null;
}

export interface UserBadgesPublicResponse {
  user_id: number;
  badges: EarnedBadge[];
  total_earned: number;
  highest_level: number;
}

export interface BadgeProgressItem {
  badge_key: string;
  name: string;
  description: string;
  level: number;
  emoji?: string | null;
  svg_key?: string | null;
  category: string;
  current_value: number;
  target_value: number;
  percentage: number;
  detail?: Record<string, unknown> | null;
}

export interface BadgeSummary {
  total_earned: number;
  total_available: number;
  by_level: Record<string, number>;
}

export interface UserBadgesDashboardResponse {
  user_id: number;
  earned: EarnedBadge[];
  progress: BadgeProgressItem[];
  summary: BadgeSummary;
}

export interface BadgeNotification {
  id: number;
  badge_key: string;
  name: string;
  description: string;
  level: number;
  emoji?: string | null;
  svg_key?: string | null;
  created_at: string;
}

export interface BadgeNotificationsListResponse {
  notifications: BadgeNotification[];
  count: number;
}

// Notification types
export interface NotificationItem {
  id: number;
  notification_type: 'comment' | 'reply' | 'like' | 'follow';
  scorecard_id: number | null;
  comment_id: number | null;
  preview_text: string | null;
  is_read: boolean;
  created_at: string;
  actor: {
    id: number;
    username: string | null;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  total: number;
  limit: number;
  offset: number;
}

// Re-export editorial zone types from canonical source
export type {
  EditorPickData as EditorialZonePickResponse,
  SpotlightData as EditorialZoneSpotlightResponse,
  EditorialZoneResponse,
} from '../types/editorial';

export interface SpotlightSuggestion {
  game_id: number;
  game_pk?: string;
  game_headline: string;
  game_date: string;
  game_venue: string;
  moments: string[];
  scorecard_count: number;
  signals: string[];
}

export interface SpotlightSuggestionsResponse {
  suggestions: SpotlightSuggestion[];
  target_date: string;
}

// Moment Uploads
export interface MomentUploadUser {
  id: number;
  username: string;
  display_name?: string | null;
  avatar_url?: string | null;
}

export interface MomentUploadGame {
  id: number;
  game_pk?: string | null;
  date: string;
  home_team_code?: string | null;
  away_team_code?: string | null;
  home_score?: number | null;
  away_score?: number | null;
}

export interface MomentUploadItem {
  scorecard_id: number;
  thumbnail_url?: string | null;
  caption?: string | null;
  attended_in_person: boolean;
  created_at: string;
  user?: MomentUploadUser | null;
  game: MomentUploadGame;
  moment_tags: string[];
}

export interface MomentUploadsResponse {
  count: number;
  category: string;
  items: MomentUploadItem[];
}

// Editor's Pick Queue
export type {
  EditorPickQueueItem,
  EditorPickQueueResponse,
  EditorPickSuggestion,
  EditorPickSuggestionsResponse,
  PickQueueUser,
  PickQueueGame,
  PickQueueScorecard,
} from '../types/editorial';
